import React from 'react';
import { isNil } from 'ramda';
import { withTheme } from 'styled-components';
import {
  string,
  number,
  objectOf,
  arrayOf,
  shape,
  bool,
  func,
} from 'prop-types';
import { scaleSqrt } from 'd3-scale';

import { getCityNameWithState } from '../../utils/format';
import { City } from '../../models';
import { isEqualInterval } from '../../utils/time';
import { getColorScale, getExtent } from '../../utils/helpers';
import Circle from './CityCircle';

const minDiameter = 10;
const maxDiameter = 45;

const MapScatterplot = ({
  cities,
  top,
  left,
  pending,
  mainValues,
  timeIntervals,
  highlightTimespans,
  selectedCityId,
  hoveredCityId,
  project,
  totalTimespan,
  showPartialData,
  theme,
  onMouseEnter,
  onMouseLeave,
  onClick,
}) => {
  if (!cities) return null;

  const [min, max] = getExtent(mainValues);
  const colorScale = getColorScale(min, max);
  const sizeScale = scaleSqrt()
    .domain([0, Math.max(Math.abs(min), Math.abs(max))])
    .range([minDiameter, maxDiameter]);

  return cities.map(({ id, name, long, lat, state, has_census_data }) => {
    const mainValue = mainValues[id];

    const size = pending
      ? 2
      : !isNil(mainValue)
        ? sizeScale(Math.abs(mainValue))
        : sizeScale.range()[0];

    const timespanInterval = timeIntervals[id];

    const isPartial =
      showPartialData &&
      timespanInterval &&
      totalTimespan &&
      !isEqualInterval(timespanInterval, totalTimespan);

    const color = isNil(mainValue)
      ? theme.colors.grey300
      : isPartial ? theme.colors.grey500 : colorScale(mainValue);

    const [x, y] = project([long, lat]);

    const point = {
      id,
      mainValue,
      sideColor: color,
      x: x + left,
      y: y + top,
      lat,
      long,
      title: getCityNameWithState(name, state),
      circleRadius: size / 2,
      timespanInterval,
      isPartial,
      highlights: highlightTimespans[id],
      has_census_data: has_census_data,
    };

    return (
      <Circle
        key={id}
        selected={id === selectedCityId || id === hoveredCityId}
        style={{
          left: `${x}px`,
          top: `${y}px`,
          width: `${size}px`,
          height: `${size}px`,
          background: color,
        }}
        onMouseEnter={() => onMouseEnter && onMouseEnter(point)}
        onMouseLeave={onMouseLeave}
        onClick={e => onClick(point, e)}
      />
    );
  });
};

MapScatterplot.propTypes = {
  cities: arrayOf(City),
  top: number.isRequired,
  left: number.isRequired,
  mainValues: objectOf(number).isRequired,
  timeIntervals: objectOf(string).isRequired,
  highlightTimespans: objectOf(
    arrayOf(shape({ value: number, timespan: string })),
  ).isRequired,
  project: func.isRequired,
  totalTimespan: string,
  showPartialData: bool,
  selectedCityId: string,
  hoveredCityId: string,
  onMouseEnter: func,
  onMouseLeave: func,
  onClick: func,
  pending: bool.isRequired,
};

export default withTheme(MapScatterplot);



// WEBPACK FOOTER //
// ./src/components/MapPage/MapScatterplot.jsx